import { Allotment } from 'allotment'
import 'allotment/dist/style.css'
import { useWorkspaceStore, WORKSPACE_PANEL_CONSTRAINTS } from '@/store/workspaceStore'
import { LeftPanel } from '../panels/LeftPanel'
import { RightPanel } from '../panels/RightPanel'
import { BottomPanel } from '../panels/BottomPanel'
import { CenterPanel } from '../panels/CenterPanel'
import { CollapsedStrip } from './CollapsedStrip'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'

interface WorkspaceLayoutProps {
  activities: ActivityWithExchanges[]
  projectName?: string
  projectDatabase?: string
  selectedProjectId: string | null
  selectedNodeId?: string | null
  selectedNodeType?: 'activity' | 'exchange' | null
  onProjectSelect: (projectId: string) => void
  onNodeClick: (nodeId: string, nodeType: 'activity' | 'exchange') => void
  onExchangeCreated?: () => void
  onActivityCreated?: () => void
  isLoading?: boolean
  // Socket state for bottom panel
  wsEvents: ToolCallEvent[]
  wsConnected: boolean
  wsConnecting: boolean
  onClearEvents: () => void
}

export function WorkspaceLayout({
  activities,
  projectName,
  projectDatabase,
  selectedProjectId,
  selectedNodeId,
  selectedNodeType,
  onProjectSelect,
  onNodeClick,
  onExchangeCreated,
  onActivityCreated,
  isLoading,
  wsEvents,
  wsConnected,
  wsConnecting,
  onClearEvents,
}: WorkspaceLayoutProps) {
  const { panels, setPanelSize, togglePanel } = useWorkspaceStore()

  const handleHorizontalChange = (sizes: number[]) => {
    // sizes array: [left, center, right] or subset if panels collapsed
    let idx = 0
    if (!panels.left.collapsed && sizes[idx] !== undefined) {
      setPanelSize('left', sizes[idx])
      idx++
    }
    // Skip center (idx++)
    idx++
    if (!panels.right.collapsed && sizes[idx] !== undefined) {
      setPanelSize('right', sizes[idx])
    }
  }

  const handleCenterVerticalChange = (sizes: number[]) => {
    // sizes array: [center content, bottom]
    if (!panels.bottom.collapsed && sizes[1] !== undefined) {
      setPanelSize('bottom', sizes[1])
    }
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Main horizontal layout: Left | Center+Bottom | Right */}
      <Allotment onChange={handleHorizontalChange} className="flex-1">
        {/* Left Panel */}
        {panels.left.collapsed ? (
          <Allotment.Pane minSize={48} maxSize={48}>
            <CollapsedStrip position="left" onExpand={() => togglePanel('left')} />
          </Allotment.Pane>
        ) : (
          <Allotment.Pane
            preferredSize={panels.left.size}
            minSize={WORKSPACE_PANEL_CONSTRAINTS.left.min}
            maxSize={WORKSPACE_PANEL_CONSTRAINTS.left.max}
          >
            <LeftPanel
              activities={activities}
              selectedProjectId={selectedProjectId}
              projectDatabase={projectDatabase}
              onProjectSelect={onProjectSelect}
              isLoadingProject={isLoading}
              onActivityCreated={onActivityCreated}
              onCollapse={() => togglePanel('left')}
              wsEvents={wsEvents}
            />
          </Allotment.Pane>
        )}

        {/* Center Column: Center Panel + Bottom Panel (vertical split) */}
        <Allotment.Pane>
          <Allotment vertical onChange={handleCenterVerticalChange}>
            {/* Center Panel - Main content */}
            <Allotment.Pane>
              <CenterPanel
                activities={activities}
                projectName={projectName}
                projectDatabase={projectDatabase}
                selectedNodeId={selectedNodeId}
                selectedNodeType={selectedNodeType}
                onNodeClick={onNodeClick}
                onExchangeCreated={onExchangeCreated}
                isLoading={isLoading}
              />
            </Allotment.Pane>

            {/* Bottom Panel - Below center only */}
            {panels.bottom.collapsed ? (
              <Allotment.Pane minSize={36} maxSize={36}>
                <CollapsedStrip position="bottom" onExpand={() => togglePanel('bottom')} />
              </Allotment.Pane>
            ) : (
              <Allotment.Pane
                preferredSize={panels.bottom.size}
                minSize={WORKSPACE_PANEL_CONSTRAINTS.bottom.min}
                maxSize={WORKSPACE_PANEL_CONSTRAINTS.bottom.max}
              >
                <BottomPanel
                  onCollapse={() => togglePanel('bottom')}
                  events={wsEvents}
                  connected={wsConnected}
                  connecting={wsConnecting}
                  onClearEvents={onClearEvents}
                  activities={activities}
                  onSelectNode={onNodeClick}
                />
              </Allotment.Pane>
            )}
          </Allotment>
        </Allotment.Pane>

        {/* Right Panel */}
        {panels.right.collapsed ? (
          <Allotment.Pane minSize={48} maxSize={48}>
            <CollapsedStrip position="right" onExpand={() => togglePanel('right')} />
          </Allotment.Pane>
        ) : (
          <Allotment.Pane
            preferredSize={panels.right.size}
            minSize={WORKSPACE_PANEL_CONSTRAINTS.right.min}
            maxSize={WORKSPACE_PANEL_CONSTRAINTS.right.max}
          >
            <RightPanel
              activities={activities}
              projectName={projectName}
              projectDatabase={projectDatabase}
              onExchangeCreated={onExchangeCreated}
              onCollapse={() => togglePanel('right')}
            />
          </Allotment.Pane>
        )}
      </Allotment>
    </div>
  )
}
